// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useState } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";

import { ApolloError, useMutation } from "@apollo/client";
import { mdiAlert } from "@mdi/js";
import styled from "styled-components/macro";
import { uid } from "utils";

import useBetaFeatures from "hooks/useBetaFeatures";
import { useFeatureGatedFilter } from "hooks/useFeatureGatedFilter";

import { UPDATE_WORKSPACE, WorkspaceClientName } from "api/workspace";

import { WorkspaceDashboard, WorkspaceDashboardInput } from "models/workspace";

import { useUnsavedTypeWellChangesGuard } from "components/arps/UnsavedTypeWellChangesPopconfirm";
import { IconSpinner } from "components/icons";
import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";
import { useWorkspaceDispatch } from "components/workspace/hooks/useWorkspaceDispatch";

import { resetForecastZoomMode } from "../../store/features";
import { RootState } from "../../store/rootReducer";
import DashboardTab from "./DashboardTab";
import { useDashboardContext, useDashboardDispatch } from "./hooks";

function DashboardNav() {
  const workspaceDispatch = useWorkspaceDispatch();
  const dispatch = useDispatch();
  const {
    workspace,
    activeDashboardId,
    isLoading: workspaceLoading,
    error: workspaceError,
    workspaceRefreshObserver
  } = useWorkspaceContext();

  const [dashboardList, setDashboardList] = useState([]);

  const [pendingDashboardId, setPendingDashboardId] = useState<string | null>(null);

  const { filterDashboards } = useFeatureGatedFilter();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (!workspace?.dashboards?.length) return;
    const filteredDashboards = filterDashboards(workspace.dashboards);
    setDashboardList(filteredDashboards);
  }, [workspace?.dashboards, filterDashboards]);

  // If the active dashboard was gated out, fall back to the first available dashboard.
  // Separate effect to avoid a loop: the fallback sets activeDashboardId,
  // which must not re-trigger the list-building effect above.
  useEffect(() => {
    if (!dashboardList.length || !activeDashboardId) return;
    const isActiveVisible = dashboardList.some(
      (d) => d.dashboardId === activeDashboardId
    );
    if (!isActiveVisible) {
      workspaceDispatch({
        payload: { activeDashboardId: dashboardList[0].dashboardId }
      });
    }
  }, [dashboardList, activeDashboardId, workspaceDispatch]);

  const { isModified: isDashboardModified } = useDashboardContext();
  const dashboardDispatch = useDashboardDispatch();
  const [updateWorkspace] = useMutation(UPDATE_WORKSPACE);
  const { hasUnsavedTypeWellChanges, shouldBlockNavigation } =
    useUnsavedTypeWellChangesGuard();
  const isForecastZoomModeActive = useSelector(
    (state: RootState) => state.charts.forecastZoomModeActive
  );

  const { hasFeature } = useBetaFeatures();

  //for initial load
  useEffect(() => {
    if (!workspace?.dashboards?.length || !activeDashboardId || !isInitialLoad) return;

    const activeDashboard = workspace.dashboards.find(
      (d) => d.dashboardId === activeDashboardId
    );

    if (activeDashboard) {
      setIsInitialLoad(false);
    }
  }, [workspace?.dashboards, activeDashboardId, hasFeature, isInitialLoad]);

  const updateDashboard = useCallback(
    (value) => {
      if (isForecastZoomModeActive) {
        dispatch(resetForecastZoomMode(true));
      }

      // Shared reset payload: clears edit mode and modified flags on any
      // confirmed navigation (FR-003, FR-005, FR-006).
      const editStateReset = {
        payload: {
          dashboardMode: "explore" as const,
          isModified: false,
          hasModifiedWidgets: false
        }
      };

      if (!isDashboardModified) {
        // If trying to switch dashboards while editing a type well with unsaved changes
        if (shouldBlockNavigation(value, setPendingDashboardId)) {
          return;
        }

        workspaceDispatch({ payload: { activeDashboardId: value } });
        // Always reset to explore on an unmodified switch (FR-006).
        dashboardDispatch(editStateReset);
      } else {
        if (
          // eslint-disable-next-line no-alert
          window.confirm(
            "Are you sure you want to go to leave without saving your changes?"
          )
        ) {
          workspaceDispatch({ payload: { activeDashboardId: value } });
          // Reset only on confirmed navigation; cancel path must leave state intact (FR-004, FR-005).
          dashboardDispatch(editStateReset);
        }
      }

      const dashboard = workspace.dashboards.find((d) => d.dashboardId === value);
      if (!dashboard) return;
    },
    [
      workspaceDispatch,
      dashboardDispatch,
      isDashboardModified,
      isForecastZoomModeActive,
      workspace?.dashboards,
      hasUnsavedTypeWellChanges,
      dispatch
    ]
  );

  const handleConfirmDashboardChange = useCallback(() => {
    if (pendingDashboardId) {
      workspaceDispatch({ payload: { activeDashboardId: pendingDashboardId } });
      // Type-well confirm path commits navigation — reset edit state (FR-003, FR-006).
      dashboardDispatch({
        payload: {
          dashboardMode: "explore",
          isModified: false,
          hasModifiedWidgets: false
        }
      });
      setPendingDashboardId(null);
    }
  }, [pendingDashboardId, workspaceDispatch, dashboardDispatch]);

  const handleCancelDashboardChange = useCallback(() => {
    setPendingDashboardId(null);
  }, []);

  const mutateWorkspace = (list: WorkspaceDashboard[]) => {
    const inputList: WorkspaceDashboardInput[] = list.reduce((acc, d) => {
      acc.push({
        dashboardId: d.dashboardId,
        favourite: false
      });
      return acc;
    }, []);

    updateWorkspace({
      variables: {
        workspace: {
          workspaceId: workspace.workspaceId,
          dashboards: inputList
        }
      },
      context: {
        clientName: WorkspaceClientName
      },
      onCompleted: () => {
        workspaceRefreshObserver.next(uid());
      },
      onError: (error: ApolloError) => {
        // eslint-disable-next-line no-console
        console.error(error.message, error);
      }
    });
  };

  const handleDragEnd = () => (result) => {
    const { source, destination } = result;

    // dropped outside
    if (!destination) return;

    // dropped at same location
    if (destination.index === source.index) return;

    // re-order list
    const nextList = [...dashboardList];
    const tab = { ...dashboardList[source.index] };
    nextList.splice(source.index, 1);
    nextList.splice(destination.index, 0, tab);
    setDashboardList(nextList);

    // update workspace
    mutateWorkspace(nextList);
  };

  const refreshWorkspace = (d) => () => {
    const nextDashboardId =
      activeDashboardId === d.dashboardId
        ? d.dashboardId
        : workspace.dashboards[0].dashboardId;

    updateDashboard(nextDashboardId);
    workspaceRefreshObserver.next(uid());
  };

  if (workspaceError) {
    return (
      <ErrorContainer>
        <Icon path={mdiAlert} size={1} />
        Error loading Workspace.
      </ErrorContainer>
    );
  }

  // TODO: Add empty dashboard component, when no dashboards in list
  if (workspaceLoading || !dashboardList.length)
    return <StyledSpinner data-testid="dashboard-nav-loading" />;

  return (
    <DragDropContext onDragEnd={handleDragEnd()}>
      <Droppable droppableId="dashboard-tabs" direction="horizontal">
        {(provided) => (
          <Wrapper
            ref={provided.innerRef}
            {...provided.droppableProps}
            // Named for the guided walkthrough, which points here to send the
            // user to a dashboard holding a particular widget.
            data-testid="dashboard-tab-strip">
            {dashboardList.map((d, index) => (
              <DashboardTab
                key={d.dashboardId}
                index={index}
                dashboard={d}
                active={activeDashboardId === d.dashboardId}
                onClick={(id) => updateDashboard(id)}
                refreshWorkspace={refreshWorkspace(d)}
                pendingDashboardId={pendingDashboardId}
                onConfirmSwitch={handleConfirmDashboardChange}
                onCancelSwitch={handleCancelDashboardChange}
              />
            ))}
            {provided.placeholder}
          </Wrapper>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default DashboardNav;

const Wrapper = styled.div`
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  background-color: var(--color-text-06);

  & > div:first-child > span::after {
    opacity: 0;
  }

  &::after,
  &::before {
    /* content: ""; */
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    height: 100%;
    background-color: var(--color-text-20);
  }
  &::after {
    right: -1px;
  }
  &::before {
    left: -1px;
  }
`;

const StyledSpinner = styled(IconSpinner)`
  align-self: flex-start;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
  text-align: left;
`;
